import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Users, Key, Mail, BarChart3, LogOut, 
  Plus, Trash2, Edit2, Download, RefreshCw, Eye, EyeOff,
  Check, X, Loader2
} from 'lucide-react';
import { 
  getAdminUser, getDashboardStats, getAccessCodes, 
  createAccessCode, deleteAccessCode, getSignups 
} from '../utils/api';

function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [accessCodes, setAccessCodes] = useState([]);
  const [signups, setSignups] = useState([]);
  const [showNewCode, setShowNewCode] = useState(false);
  const [newCode, setNewCode] = useState({ code: '', name: '', type: 'investor' });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, activeTab]);

  const checkAuth = async () => {
    const token = localStorage.getItem('korefolio_admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    try {
      const data = await getAdminUser();
      setUser(data.user);
    } catch (err) {
      localStorage.removeItem('korefolio_admin_token');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      if (activeTab === 'overview') {
        const data = await getDashboardStats();
        setStats(data);
      } else if (activeTab === 'codes') {
        const data = await getAccessCodes();
        setAccessCodes(data);
      } else if (activeTab === 'signups') {
        const data = await getSignups({ limit: 100 });
        setSignups(data.signups || []);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('korefolio_admin_token');
    navigate('/admin');
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    try {
      await createAccessCode(newCode);
      setShowNewCode(false);
      setNewCode({ code: '', name: '', type: 'investor' });
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create code');
    }
  };

  const handleDeleteCode = async (id) => {
    if (!confirm('Are you sure you want to delete this access code?')) return;
    try {
      await deleteAccessCode(id);
      loadData();
    } catch (err) {
      alert('Failed to delete code');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-dark">
        <Loader2 className="w-8 h-8 animate-spin text-kore-400" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'codes', label: 'Access Codes', icon: Key },
    { id: 'signups', label: 'Email Signups', icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-surface-dark">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-surface-card border-r border-surface-border p-4">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-kore-500 to-kore-700 flex items-center justify-center">
            <span className="text-lg font-bold">K</span>
          </div>
          <div>
            <h1 className="font-semibold">KoreFolio</h1>
            <p className="text-xs text-zinc-500">Admin Dashboard</p>
          </div>
        </div>

        <nav className="space-y-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-kore-500/20 text-kore-400'
                    : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="p-3 bg-surface-elevated rounded-lg mb-3">
            <p className="text-sm text-zinc-400 truncate">{user?.email}</p>
            <p className="text-xs text-zinc-600">{user?.role}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-zinc-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {activeTab === 'overview' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Dashboard Overview</h2>
            
            {stats && (
              <>
                <div className="grid grid-cols-4 gap-4 mb-8">
                  {[
                    { label: 'Total Signups', value: stats.stats.totalSignups, color: 'kore' },
                    { label: 'Total Sessions', value: stats.stats.totalSessions, color: 'mint' },
                    { label: 'AI Questions', value: stats.stats.totalQuestions, color: 'lavender' },
                    { label: 'Active Codes', value: stats.stats.activeAccessCodes, color: 'gold' },
                  ].map((stat) => (
                    <div key={stat.label} className="card">
                      <p className="text-sm text-zinc-500 mb-1">{stat.label}</p>
                      <p className="text-3xl font-bold text-gradient">{stat.value}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="card">
                    <h3 className="font-semibold mb-4">Recent Signups</h3>
                    <div className="space-y-2">
                      {stats.recentSignups?.slice(0, 5).map((signup) => (
                        <div key={signup._id} className="flex items-center justify-between py-2 border-b border-surface-border/50 last:border-0">
                          <span className="text-sm text-zinc-300">{signup.email}</span>
                          <span className="text-xs text-zinc-500">
                            {new Date(signup.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="card">
                    <h3 className="font-semibold mb-4">Top Access Codes</h3>
                    <div className="space-y-2">
                      {stats.topAccessCodes?.map((code) => (
                        <div key={code._id} className="flex items-center justify-between py-2 border-b border-surface-border/50 last:border-0">
                          <span className="text-sm font-mono text-kore-400">{code.code}</span>
                          <span className="text-xs text-zinc-500">{code.useCount} uses</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'codes' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Access Codes</h2>
              <button
                onClick={() => setShowNewCode(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Code
              </button>
            </div>

            {showNewCode && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card mb-6"
              >
                <form onSubmit={handleCreateCode} className="flex items-end gap-4">
                  <div className="flex-1">
                    <label className="block text-sm text-zinc-400 mb-1">Code</label>
                    <input
                      type="text"
                      value={newCode.code}
                      onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                      className="input-field uppercase"
                      placeholder="INVESTOR2026"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm text-zinc-400 mb-1">Name</label>
                    <input
                      type="text"
                      value={newCode.name}
                      onChange={(e) => setNewCode({ ...newCode, name: e.target.value })}
                      className="input-field"
                      placeholder="VC Firm Name"
                      required
                    />
                  </div>
                  <div className="w-40">
                    <label className="block text-sm text-zinc-400 mb-1">Type</label>
                    <select
                      value={newCode.type}
                      onChange={(e) => setNewCode({ ...newCode, type: e.target.value })}
                      className="input-field"
                    >
                      <option value="investor">Investor</option>
                      <option value="partner">Partner</option>
                      <option value="advisor">Advisor</option>
                      <option value="demo">Demo</option>
                      <option value="media">Media</option>
                    </select>
                  </div>
                  <button type="submit" className="btn-primary">
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNewCode(false)}
                    className="btn-secondary"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </form>
              </motion.div>
            )}

            <div className="card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Code</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Uses</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-zinc-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accessCodes.map((code) => (
                    <tr key={code._id} className="border-b border-surface-border/50 last:border-0">
                      <td className="py-3 px-4 font-mono text-kore-400">{code.code}</td>
                      <td className="py-3 px-4 text-sm text-zinc-300">{code.name}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 text-xs rounded-full bg-surface-elevated text-zinc-400">
                          {code.type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-zinc-400">{code.useCount}</td>
                      <td className="py-3 px-4">
                        {code.isActive ? (
                          <span className="flex items-center gap-1 text-accent-mint text-sm">
                            <Eye className="w-3 h-3" /> Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-zinc-500 text-sm">
                            <EyeOff className="w-3 h-3" /> Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => handleDeleteCode(code._id)}
                          className="p-1.5 hover:bg-red-500/10 text-zinc-400 hover:text-red-400 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'signups' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Email Signups</h2>
              <a
                href="/api/admin/signups/export"
                className="btn-secondary flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </a>
            </div>

            <div className="card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Access Code</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Source</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {signups.map((signup) => (
                    <tr key={signup._id} className="border-b border-surface-border/50 last:border-0">
                      <td className="py-3 px-4 text-sm text-zinc-300">{signup.email}</td>
                      <td className="py-3 px-4 text-sm text-zinc-400">{signup.name || '-'}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 text-xs rounded-full bg-surface-elevated text-zinc-400">
                          {signup.userType}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {signup.referralCode ? (
                          <span className="font-mono text-sm text-kore-400">{signup.referralCode}</span>
                        ) : (
                          <span className="text-zinc-600">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-zinc-500">{signup.source}</td>
                      <td className="py-3 px-4 text-sm text-zinc-500">
                        {new Date(signup.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminDashboard;
